// Pinterest Pin Generator using JSX/React Component
// Converts JSX component to image using html2canvas
// All data is dynamic from article/API - OPTIMIZED FOR SPEED

import { createRoot } from 'react-dom/client';
import React from 'react';
import PinterestPinTemplate from '../components/templates/PinterestPinTemplate1';
import PinterestPinTemplate2 from '../components/templates/PinterestPinTemplate2';
import PinterestPinTemplate3 from '../components/templates/PinterestPinTemplate3';
import PinterestPinTemplate4 from '../components/templates/PinterestPinTemplate4';
import PinterestPinTemplate5 from '../components/templates/PinterestPinTemplate5';
import PinterestPinTemplate6 from '../components/templates/PinterestPinTemplate6';
import PinterestPinTemplate7 from '../components/templates/PinterestPinTemplate7';
import PinterestPinTemplate8 from '../components/templates/PinterestPinTemplate8';
import PinterestPinTemplate9 from '../components/templates/PinterestPinTemplate9';
import PinterestPinTemplate10 from '../components/templates/PinterestPinTemplate10';
import PinterestPinTemplate11 from '../components/templates/PinterestPinTemplate11';
import PinterestPinTemplate12 from '../components/templates/PinterestPinTemplate12';
import PinterestPinTemplate13 from '../components/templates/PinterestPinTemplate13';
import PinterestPinTemplate14 from '../components/templates/PinterestPinTemplate14';
import PinterestPinTemplate15 from '../components/templates/PinterestPinTemplate15';
import EtsyPinTemplate1 from '../components/templates/EtsyPinTemplate1';
import EtsyPinTemplate2 from '../components/templates/EtsyPinTemplate2';
import EtsyPinTemplate3 from '../components/templates/EtsyPinTemplate3';

function getTemplate(templateId) {
  if (templateId === 'etsy_template_1') return EtsyPinTemplate1;
  if (templateId === 'etsy_template_2') return EtsyPinTemplate2;
  if (templateId === 'etsy_template_3') return EtsyPinTemplate3;
  if (templateId === 'template_2') return PinterestPinTemplate2;
  if (templateId === 'template_3') return PinterestPinTemplate3;
  if (templateId === 'template_4') return PinterestPinTemplate4;
  if (templateId === 'template_5') return PinterestPinTemplate5;
  if (templateId === 'template_6') return PinterestPinTemplate6;
  if (templateId === 'template_7') return PinterestPinTemplate7;
  if (templateId === 'template_8') return PinterestPinTemplate8;
  if (templateId === 'template_9') return PinterestPinTemplate9;
  if (templateId === 'template_10') return PinterestPinTemplate10;
  if (templateId === 'template_11') return PinterestPinTemplate11;
  if (templateId === 'template_12') return PinterestPinTemplate12;
  if (templateId === 'template_13') return PinterestPinTemplate13;
  if (templateId === 'template_14') return PinterestPinTemplate14;
  if (templateId === 'template_15') return PinterestPinTemplate15;
  return PinterestPinTemplate; // template1
}

/**
 * Load image with CORS proxy if needed
 */
function getImageUrlWithProxy(url) {
  if (!url) return null;
  // If already a data URL or local, return as is
  if (url.startsWith('data:') || url.startsWith('/') || url.startsWith('http://localhost')) {
    return url;
  }
  // Use CORS proxy for external images — q=100 preserves full quality, no format conversion
  return `https://images.weserv.nl/?url=${encodeURIComponent(url)}&q=100`;
}

/**
 * Wait for all images to load with timeout - OPTIMIZED for speed
 */
function waitForImages(element, timeout = 8000) {
  return new Promise((resolve) => {
    const startTime = Date.now();
    
    const checkComplete = () => {
      const images = element.querySelectorAll('img');
      const bgDivs = element.querySelectorAll('div[style*="backgroundImage"]');
      
      let loadedCount = 0;
      let totalImages = images.length + bgDivs.length;
      
      if (totalImages === 0) {
        resolve();
        return;
      }
      
      // Check regular images
      images.forEach(img => {
        if (img.complete && img.naturalWidth > 0) {
          loadedCount++;
        } else {
          img.onload = () => {
            loadedCount++;
            if (loadedCount >= totalImages) resolve();
          };
          img.onerror = () => {
            console.warn('Image failed to load:', img.src);
            loadedCount++;
            if (loadedCount >= totalImages) resolve();
          };
        }
      });
      
      // Check background images
      bgDivs.forEach(div => {
        const bgUrl = div.style.backgroundImage.match(/url\(["']?([^"']+)["']?\)/)?.[1];
        if (bgUrl) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            loadedCount++;
            if (loadedCount >= totalImages) resolve();
          };
          img.onerror = () => {
            console.warn('Background image failed to load:', bgUrl);
            loadedCount++;
            if (loadedCount >= totalImages) resolve();
          };
          img.src = bgUrl;
        } else {
          totalImages--;
        }
      });
      
      // Check if all already loaded
      if (loadedCount >= totalImages) {
        resolve();
        return;
      }
      
      // Timeout check - reduced to 8 seconds for faster generation
      if (Date.now() - startTime > timeout) {
        console.warn('Image loading timeout, proceeding anyway...');
        resolve();
      }
    };
    
    // Start checking immediately - reduced from 300ms to 50ms
    setTimeout(checkComplete, 50);
    
    // Also set a timeout
    setTimeout(() => {
      if (Date.now() - startTime < timeout) {
        checkComplete();
      }
    }, timeout);
  });
}

/**
 * Generate Pinterest pin image from JSX component - OPTIMIZED VERSION
 * @param {Object} params
 * @param {string} params.title - Pin title (dynamic from article)
 * @param {string} params.imageUrl - Food image URL (dynamic from article) - used for both top and bottom sections
 * @param {string} params.secondImageUrl - Optional second image URL for bottom section
 * @param {string} params.domain - Website domain (dynamic from article)
 * @param {string} params.templateUrl - Template image URL (dynamic from API)
 * @param {string} params.focusKeyword - Focus keyword for Pinterest SEO (REQUIRED)
 * @returns {Promise<string>} - Base64 PNG data URL
 */
export async function generatePinImageFromJSX({
  title, imageUrl, domain, templateId = 'template1', templateUrl = null, secondImageUrl = null, focusKeyword = '', shopName = '' }) {
  let container = null;
  let root = null;
  
  try {
    // Use focusKeyword if available, otherwise fall back to title
    const pinText = (focusKeyword || title || "").trim();
    
    if (!pinText) {
      throw new Error('No text available for pin: focusKeyword or title is required');
    }
    
    console.log('🎨 Pin generation starting...');
    console.log('🔤 Pin text:', pinText);
    console.log('🖼️ Food Image URL:', imageUrl);
    console.log('🎨 Template ID:', templateId);

    // Validate inputs
    if (!imageUrl) {
      throw new Error('Image URL is required');
    }

    // Dynamically import html2canvas
    const html2canvas = (await import('html2canvas')).default;

    // Template 1, Template 2, and Template 9 are built at the final 1080x1920 (9:16)
    // resolution — their fixed SVG overlays require capturing at that native size, unlike
    // every other template which is authored at 474x948 (1:2) and upscaled 2x during capture.
    // Template 3, Template 4, Template 5, Template 6, Template 7, and Template 8's SVG overlays
    // are natively 1000x1920, so they're captured at that native size instead of being stretched.
    // Etsy Template 1, Etsy Template 2, and Etsy Template 3's overlays are natively 4:5, captured
    // at their own native 1000x1250 for the same reason.
    const isSvgOverlayTemplate = templateId === 'template1' || templateId === 'template_2' || templateId === 'template_9';
    const isTemplate3 = templateId === 'template_3';
    const isTemplate4 = templateId === 'template_4';
    const isTemplate5 = templateId === 'template_5';
    const isTemplate6 = templateId === 'template_6';
    const isTemplate7 = templateId === 'template_7';
    const isTemplate8 = templateId === 'template_8';
    const isEtsyTemplate1 = templateId === 'etsy_template_1';
    const isEtsyTemplate2 = templateId === 'etsy_template_2';
    const isEtsyTemplate3 = templateId === 'etsy_template_3';
    const isEtsyOverlayTemplate = isEtsyTemplate1 || isEtsyTemplate2 || isEtsyTemplate3;
    const captureWidth = isSvgOverlayTemplate ? 1080 : (isTemplate3 || isTemplate4 || isTemplate5 || isTemplate6 || isTemplate7 || isTemplate8) ? 1000 : isEtsyOverlayTemplate ? 1000 : 474;
    const captureHeight = isSvgOverlayTemplate ? 1920 : (isTemplate3 || isTemplate4 || isTemplate5 || isTemplate6 || isTemplate7 || isTemplate8) ? 1920 : isEtsyOverlayTemplate ? 1250 : 948;
    const captureScale = (isSvgOverlayTemplate || isTemplate3 || isTemplate4 || isTemplate5 || isTemplate6 || isTemplate7 || isTemplate8 || isEtsyOverlayTemplate) ? 1 : 2;

    // Create a temporary container for the React component
    // IMPORTANT: Use opacity:0 instead of visibility:hidden or off-screen
    // html2canvas needs the element to be "visible" to render properly
    container = document.createElement('div');
    container.id = 'pin-generator-temp-container';
    container.style.position = 'fixed';
    container.style.left = '0';
    container.style.top = '0';
    container.style.width = `${captureWidth}px`;
    container.style.height = `${captureHeight}px`;
    container.style.zIndex = '-9999';
    container.style.opacity = '0';
    container.style.pointerEvents = 'none';
    document.body.appendChild(container);

    // Create React root and render component
    root = createRoot(container);
    
    // Use proxy URLs for images to avoid CORS issues
    const proxiedImageUrl = getImageUrlWithProxy(imageUrl);
    const proxiedTemplateUrl = templateUrl ? getImageUrlWithProxy(templateUrl) : null;

    // For split image template, use same image for both sections if only one provided
    const imageUrl1 = proxiedImageUrl || imageUrl;
    const imageUrl2 = secondImageUrl ? (getImageUrlWithProxy(secondImageUrl) || secondImageUrl) : (proxiedImageUrl || imageUrl);

    console.log('🖼️ IMAGE MAPPING DEBUG:');
    console.log('  secondImageUrl (raw input):', secondImageUrl || '(null/empty — will fallback)');
    console.log('  imageUrl1 (foodImageUrl1 → TOP):', imageUrl1);
    console.log('  imageUrl2 (foodImageUrl2 → BOTTOM):', imageUrl2);
    console.log('  imageUrl1 === imageUrl2:', imageUrl1 === imageUrl2);

    const TemplateComponent = getTemplate(templateId);

    console.log('🎨 Rendering template...');
    
    // Render with focusKeyword as the text displayed
    root.render(
      React.createElement(TemplateComponent, {
        foodImageUrl1: imageUrl1,
        foodImageUrl2: imageUrl2,
        title: pinText,
        focusKeyword: pinText,
        domain: domain,
        templateImageUrl: proxiedTemplateUrl || templateUrl,
        shopName: shopName
      })
    );

    // Wait for React to render - increased to 500ms for reliability
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Find the rendered component
    const pinElement = container.querySelector('#pinterest-pin-template');
    
    if (!pinElement) {
      throw new Error('Pin template element not found after rendering');
    }
    
    console.log('🎨 Waiting for images to load...');
    
    // Wait for images to load - increased timeout for reliability
    await waitForImages(pinElement, 15000);
    
    console.log('🎨 Images loaded, capturing with html2canvas...');

    // Convert JSX component to canvas/image using html2canvas
    // scale:2 → 948×1896px — exceeds Pinterest's recommended resolution while keeping file size small.
    // scale:4 was producing 1896×3792px (7M pixels) at q=0.97 which caused 550–800 KB WebP files.
    // Template 1, Template 2, and Template 9 are already authored at 1080x1920, so they capture at scale:1 (see captureWidth/Height/Scale above).
    const canvas = await html2canvas(pinElement, {
      width: captureWidth,
      height: captureHeight,
      scale: captureScale,
      useCORS: true,
      allowTaint: true, // Allow tainted canvas for cross-origin images
      backgroundColor: '#ffffff', // Solid background removes alpha channel — all templates are opaque
      logging: true, // Enable logging for debugging
      removeContainer: false,
      imageTimeout: 15000,
      onclone: (clonedDoc) => {
        console.log('🎨 html2canvas onclone called');
        // Inject all font stylesheets into the cloned document so fonts render correctly
        document.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
          const clonedLink = clonedDoc.createElement('link');
          clonedLink.rel = 'stylesheet';
          clonedLink.href = link.href;
          clonedDoc.head.appendChild(clonedLink);
        });
        const clonedElement = clonedDoc.querySelector('#pinterest-pin-template');
        if (clonedElement) {
          // Force all background images to be converted to img elements for better capture
          const bgDivs = clonedElement.querySelectorAll('div[style*="backgroundImage"]');
          bgDivs.forEach(div => {
            const bgUrl = div.style.backgroundImage.match(/url\(["']?([^"']+)["']?\)/)?.[1];
            if (bgUrl && !div.querySelector('img')) {
              const img = clonedDoc.createElement('img');
              img.src = bgUrl;
              img.style.position = 'absolute';
              img.style.top = '0';
              img.style.left = '0';
              img.style.width = '100%';
              img.style.height = '100%';
              img.style.objectFit = 'cover';
              img.crossOrigin = 'anonymous';
              div.appendChild(img);
            }
          });
        }
      }
    });

    // q=0.88 is the sweet spot for food photography: visually lossless, ~40% smaller than q=0.97.
    const dataUrl = canvas.toDataURL('image/webp', 0.88);
    
    console.log('✅ Pin generated! Size:', (dataUrl.length / 1024).toFixed(2), 'KB');
    
    // Quick validation - check if image is mostly black
    const isBlack = await checkIfImageIsBlack(canvas);
    if (isBlack) {
      console.warn('⚠️ Generated image appears to be mostly black, retrying with longer wait...');
      // Wait longer and try again
      await new Promise(resolve => setTimeout(resolve, 2000));
      const retryCanvas = await html2canvas(pinElement, {
        width: captureWidth,
        height: captureHeight,
        scale: captureScale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: true,
        removeContainer: false,
        imageTimeout: 20000
      });
      const retryDataUrl = retryCanvas.toDataURL('image/webp', 0.88);
      console.log('✅ Retry successful! Size:', (retryDataUrl.length / 1024).toFixed(2), 'KB');
      return retryDataUrl;
    }
    
    return dataUrl;
  } catch (error) {
    console.error('❌ Pin generation error:', error);
    
    if (error.message?.includes('html2canvas')) {
      throw new Error('html2canvas not installed. Please run: npm install html2canvas');
    }
    
    throw new Error(`Failed to generate pin: ${error.message}`);
  } finally {
    // Cleanup
    try {
      if (root) {
        root.unmount();
      }
      if (container && container.parentNode) {
        document.body.removeChild(container);
      }
    } catch (cleanupError) {
      // Silent cleanup
    }
  }
}

/**
 * Check if generated canvas is mostly black (indicates rendering failure)
 */
function checkIfImageIsBlack(canvas) {
  return new Promise((resolve) => {
    try {
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, 100, 100); // Sample top-left corner
      const data = imageData.data;
      let totalBrightness = 0;
      let pixelCount = 0;
      
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const brightness = (r + g + b) / 3;
        totalBrightness += brightness;
        pixelCount++;
      }
      
      const avgBrightness = totalBrightness / pixelCount;
      console.log('🎨 Average brightness:', avgBrightness);
      resolve(avgBrightness < 10); // Consider black if avg brightness < 10
    } catch (e) {
      resolve(false); // If we can't check, assume it's fine
    }
  });
}
